/**
 * Standalone loadOptions function for the firm-user paginated dropdown,
 * designed for use with react-select-async-paginate.
 *
 * listUsers pagination:
 *   Request  → `next` param holds the cursor from the previous response
 *   Response → `response.data.next_page` is the cursor for the next page
 *              (empty / falsy = no more pages)
 *
 * The `additional` object passed through AsyncPaginate carries the cursor
 * between page loads: { nextPage: string }
 */

import { listUsers } from 'services/users/userServices';
import { generateFullName } from 'utils/generateFullNameUtils';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Fetches one page of active firm users.
 *
 * @param {string} search    - Keyword filter
 * @param {string} nextPage  - Cursor returned by previous response ('' = first page)
 * @param {number} pageSize  - Number of records per page
 * @returns {Promise<{ options, hasMore, additional }>}
 */
const fetchFirmUsersPage = async ({ search = '', nextPage = '', pageSize = DEFAULT_PAGE_SIZE }) => {
  const response = await listUsers({
    keyWord: search,
    pagination_token: nextPage, // cursor passed to the API
    pagination_direction: 'forward',
    limit: pageSize,
    is_include_groups: false,
    is_confirmed_user: true,
    status: 'ACTIVE',
    active_groups: false,
    excludeFromLoading: true,
  });

  const users = response?.data?.firm_users || [];
  const nextCursor = response?.data?.next_token || '';

  const options = users.map((user) => ({
    label: generateFullName(user),
    value: user.firm_user_id || user.contact_id || user.client_cognito_username,
    ...user,
  }));

  return {
    options,
    hasMore: Boolean(nextCursor),
    additional: { nextPage: nextCursor },
  };
};

/**
 * loadOptions function for react-select-async-paginate.
 *
 * Signature: (searchQuery, loadedOptions, additional) => Promise<{ options, hasMore, additional }>
 *
 * @param {string} search         - Current search input
 * @param {Array}  _loadedOptions - Already-loaded options (unused — API handles dedup)
 * @param {Object} additional     - { nextPage: string } cursor carried between pages
 * @param {Object} config         - { pageSize?: number }
 */
export const loadFirmUserOptions = async (search, _loadedOptions, additional, config = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE } = config;
  const nextPage = additional?.nextPage || '';

  try {
    return await fetchFirmUsersPage({ search, nextPage, pageSize });
  } catch (error) {
    console.error('Error loading firm user options:', error);
    return { options: [], hasMore: false, additional: { nextPage: '' } };
  }
};

/**
 * Converts a raw assign_to array (as stored in messageDetails / API response)
 * into the { label, value, ...user } shape that FirmUserSelect expects as value.
 *
 * Use this helper when pre-populating a FirmUserSelect from saved data.
 *
 * @param {Array} assignTo - Array of raw user objects
 * @returns {Array<{ label: string, value: string, ...user }>}
 */
export const toFirmUserOptions = (assignTo = []) =>
  assignTo.map((user) => ({
    label: generateFullName(user),
    value: user.firm_user_id || user.contact_id || user.client_cognito_username || '',
    ...user,
  }));
